"use client";

import { PORTALS } from ".constants/portals";
import { Button, Icon, usePortalContext } from "@cloai/uikit";
import { includes, some } from "lodash";
import { useTranslations } from "next-intl";
import { FC, PropsWithChildren, useEffect, useMemo } from "react";

import { InputUndressType } from "@/.gql/graphql";
import { useAiRequestInfo } from "@/components/hooks/useAiRequestInfo";
import { useCompany } from "@/components/hooks/useCompany";
import { LoaderByHost } from "@/components/loader/LoaderByHost";
import { LoaderDraw } from "@/components/loader/LoaderDraw";
import { StorylineResultPortal } from "@/portals";
import {
  useAutoMaskContext,
  useProfileContext,
  useUndressContext,
} from "@/providers";
import { cn } from "@/utils/cn";

import { PlateError } from "./InfoPlates";
import { PickImages } from "./PickImages";
import { Rating } from "./Rating";

export const UIWrapper: FC<PropsWithChildren> = ({ children }) => {
  const t = useTranslations("HomePage");
  const profile = useProfileContext(); // Контекст профиля, чтобы определить авторизован ли пользователь
  const { openPortal } = usePortalContext();
  const {
    clickUndress,
    clickFileId,
    clickUndressStatus,
    setClickFileId,
    setUndressingsFiles,
    undressingsFiles,
    closeCustomizeSettings,
  } = useUndressContext(); // Контекст с данными о "раздевании" (`undress`)
  const { isShowDraw, isMaskLoading } = useAutoMaskContext(); // Контекст для рисования маски

  const {
    isInit,
    isLoading,
    isUndressing,
    isSuccess,
    isEndAllUndress,
    isError,
    isBlur,
  } = clickUndressStatus;

  const { companyLower } = useCompany();

  const { findCategoryById } = useAiRequestInfo();

  const isStoryline = useMemo(
    () =>
      some(
        clickUndress?.aiRequestIds,
        (id) => findCategoryById(id) === "storylines",
      ),
    [clickUndress?.aiRequestIds, findCategoryById],
  );

  /**
   * Обработчик удаления изображения:
   * - удаляем текущее изображение из массива и устанавливаем выбранным последнее.
   */
  const onRemoveFile = () => {
    const newUndressingsFiles = undressingsFiles.filter(
      (img) => img.id !== clickFileId,
    );

    setUndressingsFiles(newUndressingsFiles);

    // Если в массиве остались изображения, устанавливаем последнее как выбранное
    if (newUndressingsFiles.length > 0) {
      setClickFileId(newUndressingsFiles[newUndressingsFiles.length - 1].id);
    }

    // Если в массиве не осталось файлов, закрываем настройки кастомизации
    if (newUndressingsFiles.length === 0) {
      closeCustomizeSettings();
    }
  };

  useEffect(() => {
    if (isStoryline && isEndAllUndress) {
      openPortal(PORTALS.STORYLINE_RESULT);
    }
  }, [isEndAllUndress, isStoryline, openPortal]);

  return (
    <div
      className={cn(
        "relative flex size-full items-center justify-center overflow-hidden rounded-2xl border-2 border-card-border bg-card",
        "lg:border-0",
      )}
    >
      {children}

      {isError && <PlateError undress={clickUndress} />}

      {!isShowDraw && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute top-2 right-2 flex gap-2">
            {/* Кнопка удаления файла из массива изображений */}
            {isInit && (
              <Button
                isIconOnly
                variant="flat"
                radius="full"
                size="md"
                onClick={onRemoveFile}
              >
                <Icon icon="delete" />
              </Button>
            )}

            {/* Кнопки лайка/дизлайка */}
            {(isSuccess || isBlur) && !clickUndress?.isGuest && <Rating />}
          </div>

          {/* Кнопка открытия портала со Сторилайном */}
          {isSuccess && isStoryline && (
            <Button
              startContent={<Icon icon="cards_star" />}
              variant="shadow"
              radius="full"
              size="md"
              color="primary"
              onClick={() => openPortal(PORTALS.STORYLINE_RESULT)}
              loading={!isEndAllUndress}
              disabled={isLoading}
              className="absolute top-2 left-2 max-w-1/2"
            >
              {isEndAllUndress ? t("enjoyStoryline") : t("writingStoryline")}
            </Button>
          )}

          {/* Лоадер дефолтный */}
          {((isLoading && !isMaskLoading) || isUndressing) && (
            <LoaderByHost host={companyLower} />
          )}

          {/* Миниатюры всех изображений в массиве с возможностью выбора "текущего" */}
          {profile &&
            !includes(
              [
                InputUndressType.Aniamtion,
                InputUndressType.Clip,
                InputUndressType.Video,
              ],
              clickUndress?.undressType,
            ) && <PickImages />}
        </div>
      )}

      {/* Лоадер получения автомаски */}
      {isShowDraw && isLoading && isMaskLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoaderDraw />
        </div>
      )}
      {isStoryline && <StorylineResultPortal />}
    </div>
  );
};
